/**
 * ModuleManager - Lightweight module lifecycle orchestrator
 *
 * Provides a consistent way to register modules, initialize them with an
 * application context, and mount/activate/unmount them.
 */

export class ModuleManager {
  constructor(context) {
    this.context = context || {};
    /** @type {Map<string, any>} */
    this.modules = new Map();
    this.current = null;
  }

  /** Register a module instance under an id */
  register(id, instance) {
    if (!id || !instance) return;
    this.modules.set(id, instance);
    return instance;
  }

  /** Initialize all registered modules (best-effort) */
  async initializeAll() {
    for (const [id, mod] of this.modules.entries()) {
      try {
        if (typeof mod.initialize === 'function') {
          // Pass context for DI; modules can ignore it safely
          await mod.initialize(this.context);
        }
      } catch (e) {
        // Keep going; a module failing init should not break the app
        try { console.error(`[ModuleManager] Failed to initialize ${id}:`, e); } catch(_) {}
      }
    }
  }

  /** Mount a module into a container */
  async mount(id, container) {
    const mod = this.modules.get(id);
    if (!mod || typeof mod.mount !== 'function') return;
    try {
      await mod.mount(container);
      this.current = id;
    } catch (e) {
      try { console.error(`[ModuleManager] Failed to mount ${id}:`, e); } catch(_) {}
    }
  }

  /** Activate a module (optional lifecycle) */
  activate(id) {
    const mod = this.modules.get(id);
    if (!mod || typeof mod.activate !== 'function') return;
    try { mod.activate(); } catch (e) { try { console.error(`[ModuleManager] Failed to activate ${id}:`, e); } catch(_) {} }
  }

  /** Unmount currently mounted module if it supports unmount */
  async unmountCurrent() {
    if (!this.current) return;
    const mod = this.modules.get(this.current);
    if (mod && typeof mod.unmount === 'function') {
      try { await mod.unmount(); } catch(_) {}
    }
    this.current = null;
  }

  get(id) { return this.modules.get(id); }
  has(id) { return this.modules.has(id); }
  list() { return Array.from(this.modules.keys()); }
}

export default ModuleManager;

